'use client';

import { useCallback, useEffect, useState } from 'react';

import { CommonUtils } from '../../(common-lib)/CommonUtils';
import type { State } from '../../(common-lib)/LoanData';
import { ApiCache } from '../ApiCache';
import type { AddressComponent, AddressValue, PlaceDetails, StateOption } from './types';

export type useStateOptionsResult = {
  stateOptions: State[];
  isLoading: boolean;
  error: Error | null;
};

export function useStateOptions(): useStateOptionsResult {
  const [stateOptions, setStateOptions] = useState<State[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const states = await ApiCache.getStates();
      setStateOptions(Array.isArray(states) ? states : []);
    } catch (e) {
      CommonUtils.logError('Error fetching state options', e);
      setError(e instanceof Error ? e : new Error(String(e)));
      setStateOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { stateOptions, isLoading, error };
}

export const normalizeZip5 = (value: string): string => {
  return (value || '').replace(/\D/g, '').slice(0, 5);
};

export const normalizeAddressValue = (
  value: Partial<AddressValue> | undefined,
  stateOverride?: string
): AddressValue => {
  return {
    line1: value?.line1 || '',
    line2: value?.line2 || '',
    city: value?.city || '',
    state: stateOverride || value?.state || '',
    zip: normalizeZip5(value?.zip || ''),
  };
};

export const matchStateKey = (
  input: string | undefined,
  options: StateOption[]
): string | undefined => {
  const normalized = (input || '').trim().toLowerCase();
  if (!normalized) return undefined;

  const match = options.find((option) => {
    return option.key?.toLowerCase() === normalized
      || option.code?.toLowerCase() === normalized
      || option.display?.toLowerCase() === normalized;
  });
  return match?.key;
};

const getStateFromAddressComponents = (components?: AddressComponent[]): string | undefined => {
  if (!Array.isArray(components)) return undefined;
  const stateComponent = components.find((component) =>
    Array.isArray(component?.types) && component.types.includes('administrative_area_level_1')
  );
  return stateComponent?.shortText || stateComponent?.longText;
};

export const buildAddressValueFromPlaceDetails = (
  details: PlaceDetails,
  current: AddressValue,
  options: StateOption[] = [],
  stateLock?: string
): AddressValue => {
  const currentValue = normalizeAddressValue(current);

  const stateInput = getStateFromAddressComponents(details.addressComponents)
    || details.stateCode
    || details.stateName;

  const matchedStateKey = matchStateKey(stateInput, options);

  return {
    line1: details.addressLine1 || details.formattedAddress || currentValue.line1,
    line2: details.addressLine2 || currentValue.line2,
    city: details.city || currentValue.city,
    zip: normalizeZip5(details.zipCode || currentValue.zip),
    state: stateLock || matchedStateKey || currentValue.state || '',
  };
};
